import { createContext, FC, useContext, useState } from "react";
import { SapTransactionStatistics, SapTransactionStatisticsByTime, SapTransLog } from "../models/SapTransLog";
import axios, { AxiosRequestConfig } from 'axios';
import useToken from "../hooks/useToken";
import { SapTransLogQuery } from "../models/SapTransLogQuery";
import { ReactFCC } from "../types/react";

export type TransLogContextType = {
    loading: boolean;
    searching: boolean;
    exporting: boolean;
    logs: SapTransLog[];
    statistics: SapTransactionStatistics[];
    statisticsReport: SapTransactionStatisticsByTime[];
    detail: string;
    detailLog: SapTransLog;
    detailNotFound: boolean;
    searchTransLogs: (query: SapTransLogQuery) => void;
    searchStatistics: (query: SapTransLogQuery) => void;
    searchStatisticsReport: (query: SapTransLogQuery) => void;
    downloadTransLogs: (query: SapTransLogQuery) => Promise<any>;
    TransLogDetail: (log: SapTransLog) => void;
};

const TransLogContextDefaultState: TransLogContextType = {
    loading: false,
    searching: false,
    exporting: false,
    logs: [],
    statistics: [] as SapTransactionStatistics[],
    statisticsReport: [] as SapTransactionStatisticsByTime[],
    detail: "",
    detailLog: {} as SapTransLog,
    detailNotFound: false,
    searchTransLogs: (query: SapTransLogQuery) => { },
    searchStatistics: (query: SapTransLogQuery) => { },
    searchStatisticsReport: (query: SapTransLogQuery) => { },
    downloadTransLogs: (query: SapTransLogQuery) => new Promise(() => ""),
    TransLogDetail: (log: SapTransLog) => { }
};

export const TransLogContext = createContext(TransLogContextDefaultState);

const TransLogProvider: ReactFCC = ({ children }: any) => {
    const [loading, setLoading] = useState(TransLogContextDefaultState.loading);
    const [searching, setSearching] = useState(TransLogContextDefaultState.searching);
    const [exporting, setExporting] = useState(TransLogContextDefaultState.exporting);
    const [logs, setLogs] = useState([] as any[]);
    const [statistics, setStatistics] = useState([] as SapTransactionStatistics[]);
    const [statisticsReport, setStatisticsReport] = useState([] as SapTransactionStatisticsByTime[]);
    const [detail, setDetail] = useState("");
    const [detailLog, setDetailLog] = useState({} as SapTransLog);
    const [detailNotFound, setDetailNotFound] = useState(false);
    const [getToken] = useToken();

    const searchTransLogs = async (query: SapTransLogQuery) => {
        setSearching(true);
        const config: AxiosRequestConfig = {
            method: "POST",
            url: `${process.env.REACT_APP_BACKEND_API_URL}/translog`,
            data: query,
            headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${await getToken()}` }
        };
        const response = await axios.request<SapTransLog[]>(config);
        response.status === 200 && setLogs(dataProcess(response.data));
        setSearching(false);
    };

    const searchStatistics = async (query: SapTransLogQuery) => {
        setSearching(true);
        const config: AxiosRequestConfig = {
            method: "POST",
            url: `${process.env.REACT_APP_BACKEND_API_URL}/translog/statistics`,
            data: query,
            headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${await getToken()}` }
        };
        const response = await axios.request<SapTransactionStatistics[]>(config);
        response.status === 200 && setStatistics(response.data);
        setSearching(false);
    };

    const searchStatisticsReport = async (query: SapTransLogQuery) => {
        setSearching(true);
        const config: AxiosRequestConfig = {
            method: "POST",
            url: `${process.env.REACT_APP_BACKEND_API_URL}/translog/report`,
            data: query,
            headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${await getToken()}` }
        };
        const response = await axios.request<SapTransactionStatisticsByTime[]>(config);
        response.status === 200 && setStatisticsReport(response.data);
        setSearching(false);
    };

    const downloadTransLogs = async (query: SapTransLogQuery) => {
        query.timezoneOffset = (new Date()).getTimezoneOffset();

        setExporting(true);
        const config: AxiosRequestConfig = {
            method: "POST",
            url: `${process.env.REACT_APP_BACKEND_API_URL}/translog/download`,
            data: query,
            headers: { 'Accept': 'text/csv', 'Authorization': `Bearer ${await getToken()}` }
        };
        const response = await axios.request(config);
        setExporting(false);
        return response.status === 200 ? response.data : "";
    };

    const TransLogDetail = async (log: SapTransLog) => {
        setDetail("");
        setDetailNotFound(false);
        setDetailLog(log);
        const config: AxiosRequestConfig = {
            method: "POST",
            url: `${process.env.REACT_APP_BACKEND_API_URL}/translog/detail`,
            //Give dummy value to those date time fields to avoid the potential type mismatch at backend
            //In some cases, frontend will pass the invalid date string, in that case, the whole body will be NULL
            data: { ...log, transactionDate: (new Date()).toISOString(), insertedDate: (new Date()).toISOString() },
            headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${await getToken()}` }
        };
        const response = await axios.request<string>(config);
        response.status === 200 && setDetail(response.data);
        response.status === 204 && setDetailNotFound(true);
    };

    const dataProcess = (logs: SapTransLog[]) => {
        if (!logs) {
            return [];
        }

        logs.forEach(logItem => {
            if (logItem.transactionDate) {
                var localDate = new Date(logItem.transactionDate + "Z");
                logItem.transactionDate = localDate.toLocaleString();
            }
        })

        return logs;
    }

    return <TransLogContext.Provider value={{ loading, searching, exporting, logs, detail, statistics, statisticsReport, detailLog, detailNotFound, searchTransLogs, downloadTransLogs, TransLogDetail, searchStatistics, searchStatisticsReport }}>
        {children}
    </TransLogContext.Provider>
};

export default TransLogProvider;
export const useTransLog = () => useContext(TransLogContext);